import { Button } from "@/components/ui/button"; // Adjust the import path if needed
import {
  useGetActiveSubscriptionPlans,
  usePurchaseNewPlan,
  usePurchaseSubscriptionSession,
} from "@/hooks/use-subscription";
import { cn, extractErrorMessage } from "@/lib/utils";
import { PlacementType } from "@/types/inboxPlacements.types";
import {
  PaymentInterval,
  SubscriptionStatus,
} from "@/types/subscription.types";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Plan, PlanCategory, PlanPrice } from "./pricing-data";
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BuyMoreMailboxesDialog } from "./buy-more-mailboxes";

type Props = {
  plan: Plan;
  interval: keyof PlanPrice;
  count: number;
  index: number;
  lastPlanName: string;
  selectedType: string;
};

const PlanCard = ({
  plan,
  interval,
  count,
  index,
  lastPlanName,
  selectedType,
}: Props) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { mutateAsync: purchaseSubscription, isPending: isPurchasing } =
    usePurchaseSubscriptionSession();

  const { mutateAsync: buyNewPlan, isPending: isBuyingNewPlan } =
    usePurchaseNewPlan();

  const { data: activePlans } = useGetActiveSubscriptionPlans();

  const isPurchased = useMemo(() => {
    if (!activePlans?.length) return false;

    return activePlans.some(
      (subscription) =>
        subscription.interval === interval &&
        subscription.plans.some(
          (purchasedPlan) => purchasedPlan.planId?.planType === plan.type
        )
    );
  }, [activePlans, plan.type, interval]);

  const handlePurchase = async () => {
    try {
      if (plan.type === "CUSTOM") {
        toast.error("Contact sales to purchase a custom plan");
        return;
      }

      if (!interval) {
        toast.error("Please select an interval");
        return;
      }

      if (count <= 0) {
        toast.error("Please enter a valid quantity");
        return;
      }

      // check if it is first buy
      const isFirstBuy = activePlans?.length === 0;

      if (!isFirstBuy) {
        setShowConfirmation(true);
        return;
      }

      // buy plan with checkout session
      await purchaseSubscription({
        planType: plan.type,
        interval: interval,
        quantity: count,
      });

      window.cioanalytics?.track("plan_purchased", {
        isFirstBuy,
        plan: plan.type,
        interval: interval,
        quantity: count,
      });

      toast.success("Plan purchased successfully.");
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to purchase plan.");
      toast.error(message);
    }
  };

  const handleConfirmBuy = async () => {
    try {
      // buy new plan without checkout session, charged with default payment method
      await buyNewPlan({
        planType: plan.type,
        interval: interval,
        quantity: count,
      });

      toast.success("Plan purchased successfully.");

      window.cioanalytics?.track("plan_purchased", {
        isFirstBuy: false,
        plan: plan.type,
        interval: interval,
        quantity: count,
      });
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to purchase plan.");
      toast.error(message);
    } finally {
      setShowConfirmation(false);
    }
  };

  const isLoading = isPurchasing || isBuyingNewPlan;

  // Calculate the savings
  const calculateSavings = () => {
    if (plan.price[interval] === "Free" || plan.price[interval] === "Custom") {
      return { amount: 0, percentage: 0 };
    }

    const regularPrice = parseFloat(plan.price[interval]);
    const discountedPrice = parseFloat(plan.earlyBirdPrice[interval]);
    const savingsAmount = (regularPrice - discountedPrice) * count;
    const savingsPercentage = Math.round(
      ((regularPrice - discountedPrice) / regularPrice) * 100
    );

    return {
      amount: savingsAmount.toFixed(2),
      percentage: savingsPercentage,
    };
  };

  const currentSubsAndPlan = useMemo(() => {
    if (!activePlans?.length) return null;

    const currentSubs = activePlans.find(
      (subscription) => subscription.interval === interval
    );

    if (!currentSubs) return null;

    const currentPlan = currentSubs.plans.find(
      (subscription) => subscription.planId?.planType === plan.type
    );

    if (!currentPlan) return null;

    return {
      currentSubs,
      currentPlan,
    };
  }, [activePlans, interval, plan.type]);

  const savings = calculateSavings();

  // Get interval-based discount percentage
  const getIntervalDiscount = (interval: keyof PlanPrice): number => {
    switch (interval) {
      case PaymentInterval.QUARTERLY:
        return 10;
      case PaymentInterval.SEMI_YEARLY:
        return 15;
      case PaymentInterval.ANNUAL:
        return 20;
      default:
        return 0;
    }
  };

  const intervalDiscount = getIntervalDiscount(interval);

  return (
    <div
      className={cn(
        "relative rounded-2xl border p-6 h-full",
        plan.popular && "border-primary/50"
        // partiallyActivePlan && "border-2 bg-primary/10 border-primary/50"
      )}
    >
      <div className="absolute top-3 right-3 flex justify-center">
        <span className="bg-primary/10 font-medium text-primary text-xs px-3 py-1 rounded-md">
          Early Bird Offer
        </span>
      </div>
      <div className="mb-6 relative z-10">
        <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
        <p className="text-muted-foreground text-sm">{plan.description}</p>
      </div>
      <div className="mb-6 flex items-baseline gap-0">
        {plan.price[interval] !== "Free" &&
          plan.price[interval] !== "Custom" && (
            <span className="text-2xl font-medium text-foreground">$</span>
          )}

        <div className="relative text-4xl font-bold text-foreground mr-2">
          {parseFloat(parseFloat(plan.earlyBirdPrice[interval]).toFixed(2))}
        </div>

        <div className="relative text-xl font-semibold text-muted-foreground mr-2">
          <div className="absolute top-1/2 -translate-y-1/2 -rotate-12 scale-125 border-b h-[2px] w-full bg-muted-foreground"></div>
          {parseFloat(parseFloat(plan.price[interval]).toFixed(2))}
        </div>

        {plan.price[interval] !== "Free" &&
          plan.price[interval] !== "Custom" && (
            <span className="text-muted-foreground text-sm">
              per month
            </span>
          )}
      </div>

      {/* Display for 1 mailbox/domain with savings calculation */}
      <div className="bg-primary/10 dark:bg-primary/10 px-4 py-2 rounded-lg mb-2">
        <div className="flex justify-between items-center">
          <span className="text-sm">
            {count} Mailbox
            {count > 1 ? "es" : ""}:
          </span>
          <div className="flex items-center">
            <span className="text-sm line-through text-muted-foreground mr-2">
              ${(parseFloat(plan.price[interval]) * count).toFixed(2)}
            </span>

            <div className="flex items-center">
              <span className="text-lg font-medium">
                $
                {(parseFloat(plan.earlyBirdPrice[interval]) * count).toFixed(2)}
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                /mailbox
              </span>
            </div>
          </div>
        </div>
        {intervalDiscount > 0 && (
          <div className="text-sm text-green-600 flex justify-between">
            <span>You save:</span>
            <span className="font-medium">
              
              {savings.percentage}% off + {intervalDiscount}% EXTRA
            </span>
          </div>
        )}
      </div>

     
      {currentSubsAndPlan ? (
        currentSubsAndPlan.currentSubs.isLatestInvoicePaid ||
        currentSubsAndPlan.currentSubs.status !== SubscriptionStatus.PENDING ? (
          <BuyMoreMailboxesDialog
            currentPlan={currentSubsAndPlan.currentPlan}
            subscriptionId={currentSubsAndPlan.currentSubs._id}
            interval={interval}
            quantity={count}
            className="w-full"
            variant="default"
          />
        ) : (
          <Link to={`/billing`}>
            <Button
              variant={"secondary"}
              className="w-full disabled:opacity-100 bg-destructive/10 text-destructive"
              disabled
            >
              Unpaid Invoice
            </Button>
          </Link>
        )
      ) : (
        <Button
          variant={"default"}
          onClick={handlePurchase}
          className={cn("w-full bg-blue-600 hover:bg-blue-700 text-white")}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Buying...
            </>
          ) : (
            "Buy Now"
          )}
        </Button>
      )}

      <hr className="my-4 border-dashed" />

      {index !== 0 && (
        <p className="text-muted-foreground text-sm mb-4">
          Everything in {lastPlanName}, plus:
        </p>
      )}
      <ul className="space-y-2 text-sm">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <Check className="w-5 h-5 mt-1 text-primary shrink-0" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {showConfirmation && (
        <ConfirmationDialog
          showConfirmation={showConfirmation}
          setShowConfirmation={setShowConfirmation}
          handleConfirmBuy={handleConfirmBuy}
        />
      )}
    </div>
  );
};

const ConfirmationDialog = ({
  showConfirmation,
  setShowConfirmation,
  handleConfirmBuy,
}: {
  showConfirmation: boolean;
  setShowConfirmation: (show: boolean) => void;
  handleConfirmBuy: () => void;
}) => {
  return (
    <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Your Purchase</AlertDialogTitle>
          <AlertDialogDescription>
            This action will complete your order and charge your default payment
            method. Do you wish to proceed?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="default" onClick={handleConfirmBuy}>
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PlanCard;
